import React, { useState, useEffect } from 'react';
import { Album } from '../../types/album';
import { theme } from '../../styles/theme';
import { useWindowSize } from '../../hooks/useWindowSize';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import AlbumNavigation from './AlbumNavigation';
import ShelfLocation from './ShelfLocation';
import AlbumEditForm from './AlbumEditForm';
import AudioPreview from '../AudioPreview/AudioPreview';
import { ShelfConfig } from '../../utils/shelfCalculation';
import { useAuth } from '../../contexts/AuthContext';

interface AlbumDetailProps {
  album: Album;
  albums: Album[]; // Complete collection for Kallax calculations
  filteredAlbums?: Album[]; // Filtered albums for navigation
  shelfConfig: ShelfConfig;
  onAlbumChange: (newAlbum: Album) => void;
  onAlbumUpdate: (album: Album) => Promise<void>;
  onClose: () => void;
  onShowShelfSettings: () => void;
  showShelfSettings?: boolean;
}

const AlbumDetail: React.FC<AlbumDetailProps> = ({
  album,
  albums,
  filteredAlbums,
  shelfConfig,
  onAlbumChange,
  onAlbumUpdate,
  onClose,
  onShowShelfSettings,
  showShelfSettings = true
}) => {
  const { isAdmin } = useAuth();
  const { isMobile, isTablet } = useWindowSize();
  
  // Touch gesture handlers for mobile navigation
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => {
      if (isMobile && canNavigateRight) {
        handleNavigateRight();
      }
    },
    onSwipeRight: () => {
      if (isMobile && canNavigateLeft) {
        handleNavigateLeft();
      }
    },
    threshold: 100,
    restrain: 150,
  });
  // Helper function to format multiple artists nicely
  const formatArtists = (artists: string[]): string => {
    if (!artists || artists.length === 0) return 'Unknown Artist';
    if (artists.length === 1) return artists[0];
    if (artists.length === 2) return `${artists[0]} & ${artists[1]}`;
    
    // For 3+ artists: "Artist A, Artist B & Artist C"
    const lastArtist = artists[artists.length - 1];
    const otherArtists = artists.slice(0, -1).join(', ');
    return `${otherArtists} & ${lastArtist}`;
  };
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    // Convert blob to object URL for display, fallback to direct URL
    if (album.coverImageBlob && album.coverImageBlob.size > 0) {
      const url = URL.createObjectURL(album.coverImageBlob);
      setImageUrl(url);
      
      return () => URL.revokeObjectURL(url);
    } else if (album.coverImageUrl) {
      // Use direct URL as fallback
      setImageUrl(album.coverImageUrl);
    } else {
      setImageUrl('');
    }
  }, [album.coverImageBlob, album.coverImageUrl]);

  // Find current album index for navigation (use filtered albums if available)
  const navigationAlbums = filteredAlbums || albums;
  const currentIndex = navigationAlbums.findIndex(a => a.id === album.id);
  const canNavigateLeft = currentIndex > 0;
  const canNavigateRight = currentIndex < navigationAlbums.length - 1;

  const handleNavigateLeft = () => {
    if (canNavigateLeft) {
      setIsEditing(false); // Exit edit mode when navigating
      onAlbumChange(navigationAlbums[currentIndex - 1]);
    }
  };

  const handleNavigateRight = () => {
    if (canNavigateRight) {
      setIsEditing(false); // Exit edit mode when navigating
      onAlbumChange(navigationAlbums[currentIndex + 1]);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isEditing) return; // Don't navigate while editing
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        handleNavigateLeft();
        break;
      case 'ArrowRight':
        event.preventDefault();
        handleNavigateRight();
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, isEditing]);

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: theme.zIndex.modal,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: isMobile ? theme.spacing.sm : theme.spacing.lg,
  };

  const contentStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.xl,
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: isMobile ? '100%' : (isTablet ? '90vw' : '1200px'),
    height: isMobile ? '100%' : 'auto',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    overflow: 'hidden',
    position: 'relative',
  };

  const leftColumnStyle: React.CSSProperties = {
    flex: isMobile ? '0 0 auto' : (isTablet ? '0 0 300px' : '0 0 400px'),
    padding: isMobile ? theme.spacing.md : theme.spacing.xl,
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    alignItems: isMobile ? 'flex-start' : 'center',
    backgroundColor: theme.colors.background,
    gap: isMobile ? theme.spacing.md : 0,
  };

  const rightColumnStyle: React.CSSProperties = {
    flex: 1,
    padding: isMobile ? theme.spacing.md : theme.spacing.xl,
    overflowY: 'auto',
    position: 'relative',
    minHeight: isMobile ? '0' : 'auto',
  };

  const coverImageStyle: React.CSSProperties = {
    width: isMobile ? '120px' : '100%',
    maxWidth: isMobile ? '120px' : (isTablet ? '250px' : '350px'),
    height: isMobile ? '120px' : 'auto',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.xl,
    marginBottom: isMobile ? 0 : theme.spacing.lg,
    flexShrink: 0,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: isMobile ? '1.5rem' : (isTablet ? '2rem' : '2.5rem'),
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: theme.spacing.sm,
    lineHeight: 1.2,
  };

  const artistStyle: React.CSSProperties = {
    fontSize: isMobile ? '1.1rem' : (isTablet ? '1.25rem' : '1.5rem'),
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.lg,
  };

  const yearStyle: React.CSSProperties = {
    fontSize: isMobile ? '1rem' : '1.25rem',
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.xl,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xl,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const tagContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  };

  const tagStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primaryLight,
    color: 'white',
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '0.875rem',
    fontWeight: '500',
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: theme.spacing.md,
    right: theme.spacing.md,
    background: isMobile ? 'rgba(0, 0, 0, 0.8)' : 'none',
    color: isMobile ? 'white' : theme.colors.textMuted,
    fontSize: isMobile ? '2rem' : '1.5rem',
    padding: isMobile ? theme.spacing.md : theme.spacing.sm,
    borderRadius: theme.borderRadius.lg,
    cursor: 'pointer',
    zIndex: 10,
    minHeight: isMobile ? '44px' : 'auto',
    minWidth: isMobile ? '44px' : 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const editButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    color: 'white',
    padding: isMobile ? `${theme.spacing.md} ${theme.spacing.lg}` : `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    fontSize: isMobile ? '1rem' : '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    minHeight: isMobile ? '44px' : 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div style={containerStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={contentStyle} {...(isMobile ? swipeHandlers : {})}>
        <button style={closeButtonStyle} onClick={onClose}>
          ×
        </button>

        <AlbumNavigation
          canNavigateLeft={canNavigateLeft}
          canNavigateRight={canNavigateRight}
          onNavigateLeft={handleNavigateLeft}
          onNavigateRight={handleNavigateRight}
          currentIndex={currentIndex}
          totalAlbums={navigationAlbums.length}
        />

        <div style={leftColumnStyle}>
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={`${album.artist} - ${album.title}`}
              style={coverImageStyle}
            />
          ) : (
            <div style={{
              ...coverImageStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.colors.surfaceLight,
              color: theme.colors.textMuted
            }}>
              No Cover Image
            </div>
          )}

          {isMobile && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={titleStyle}>{album.title}</div>
              <div style={artistStyle}>{formatArtists(album.artists || [album.artist])}</div>
              <div style={yearStyle}>
                <span>{String(album.year)}</span>
                {(() => {
                  const hasValidReleaseYear = album.releaseYear && 
                                            typeof album.releaseYear === 'number' && 
                                            album.releaseYear > 0 && 
                                            album.releaseYear !== album.year;
                  
                  return hasValidReleaseYear ? (
                    <span style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginLeft: theme.spacing.sm }}>
                      (pressing: {album.releaseYear})
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
          )}

          {!isMobile && (
            <ShelfLocation 
              album={album}
              allAlbums={albums}
              shelfConfig={shelfConfig}
              onShowSettings={isAdmin ? onShowShelfSettings : () => {}}
            />
          )}
        </div>

        <div style={rightColumnStyle}>
          {isEditing ? (
            <AlbumEditForm
              album={album}
              onSave={(updatedAlbum) => {
                onAlbumUpdate(updatedAlbum);
                setIsEditing(false);
              }}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <>
              {!isMobile && (
                <>
                  <div style={titleStyle}>{album.title}</div>
                  <div style={artistStyle}>{formatArtists(album.artists || [album.artist])}</div>
                  <div style={yearStyle}>
                    <span>{String(album.year)}</span>
                    {(() => {
                      // Only show pressing year if it's a valid year and different from main year
                      const hasValidReleaseYear = album.releaseYear && 
                                                typeof album.releaseYear === 'number' && 
                                                album.releaseYear > 0 && 
                                                album.releaseYear !== album.year;
                      
                      return hasValidReleaseYear ? (
                        <span style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginLeft: theme.spacing.sm }}>
                          (pressing: {album.releaseYear})
                        </span>
                      ) : null;
                    })()}
                  </div>
                </>
              )}

              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>
                  <span>Genres</span>
                  {isAdmin && (
                    <button style={editButtonStyle} onClick={() => setIsEditing(true)}>
                      Edit
                    </button>
                  )}
                </div>
                <div style={tagContainerStyle}>
                  {album.genres.length > 0 ? (
                    album.genres.map((genre, index) => (
                      <span key={index} style={tagStyle}>{genre}</span>
                    ))
                  ) : (
                    <span style={{ color: theme.colors.textMuted }}>No genres specified</span>
                  )}
                </div>
              </div>

              {album.tracklist && album.tracklist.length > 0 && (
                <div style={sectionStyle}>
                  <div style={sectionTitleStyle}>
                    <span>Tracklist ({album.tracklist.length} tracks)</span>
                  </div>
                  <div style={{
                    backgroundColor: theme.colors.background,
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.border}`,
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {album.tracklist.map((track, index) => (
                      <div
                        key={index}
                        style={{
                          padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                          borderBottom: index < album.tracklist!.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing.sm
                        }}
                      >
                        <div style={{
                          fontSize: '0.75rem',
                          color: theme.colors.textMuted,
                          minWidth: '3rem',
                          textAlign: 'right'
                        }}>
                          {track.position}
                        </div>
                        <div style={{ flex: 1, fontSize: '0.875rem', color: theme.colors.text }}>
                          {track.title}
                          {track.artists && track.artists.length > 0 && (
                            <div style={{
                              fontSize: '0.75rem',
                              color: theme.colors.textSecondary,
                              marginTop: '2px'
                            }}>
                              {track.artists.join(', ')}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                          {track.previewUrl && (
                            <AudioPreview 
                              previewUrl={track.previewUrl}
                              trackTitle={track.title}
                            />
                          )}
                          {track.duration && (
                            <div style={{
                              fontSize: '0.75rem',
                              color: theme.colors.textMuted,
                              minWidth: '3rem',
                              textAlign: 'right'
                            }}>
                              {track.duration}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {album.customSortName && (
                <div style={sectionStyle}>
                  <div style={sectionTitleStyle}>
                    <span>Custom Sort Name</span>
                  </div>
                  <div style={{ 
                    backgroundColor: theme.colors.surfaceLight, 
                    padding: theme.spacing.md,
                    borderRadius: theme.borderRadius.md,
                    fontFamily: 'monospace'
                  }}>
                    {album.customSortName}
                  </div>
                </div>
              )}

              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>
                  <span>Collection Info</span>
                </div>
                <div style={{ color: theme.colors.textSecondary, lineHeight: 1.6 }}>
                  {album.discogsId && (
                    <div>Discogs ID: {album.discogsId}</div>
                  )}
                  <div>Sort Key: <code style={{ 
                    backgroundColor: theme.colors.surfaceLight,
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: '0.8rem'
                  }}>{album.sortKey}</code></div>
                  {album.isVariousArtists && (
                    <div style={{ color: theme.colors.accent, fontWeight: '500' }}>
                      Various Artists Compilation
                    </div>
                  )}
                </div>
              </div>

              {isMobile && (
                <ShelfLocation 
                  album={album}
                  allAlbums={albums}
                  shelfConfig={shelfConfig}
                  onShowSettings={isAdmin ? onShowShelfSettings : () => {}}
                />
              )}
            </>
          )}
        </div>
      </div>
      
    </div>
  );
};

export default AlbumDetail;